// "Scan RF" from anywhere that is not the RF panel — the Message Box, where an
// admin watching `RfV(...)` lines wants to turn one into a named sensor without
// leaving the console, and the receiver card, where an unknown sensor offers
// "Add".
//
// Self-contained: fetches the named codes when opened (so a known sensor is
// refused), runs RfScanDialog, then opens the saved sensor's full settings for
// recipients and a test — the same hand-off as RfDevicesPanel.

import React, { useState } from 'react';
import { Button, IconButton, Tooltip } from '@mui/material';
import SensorsIcon from '@mui/icons-material/Sensors';
import { getRfDevices } from '../../core/api/api';
import { invalidateRfDirectory } from '../../core/hooks/useRfDirectory';
import RfScanDialog from './RfScanDialog';
import RfSensorDialog from './RfSensorDialog';

const fetchAll = (body) => getRfDevices(body)
  .then((r) => (Array.isArray(r.data) ? r.data : []))
  .catch(() => []);

/**
 * @param iconOnly     a bare icon (for an app bar) instead of a button
 * @param receiverUno  listen to this receiver only ('' = every receiver)
 * @param locationId   saved on the sensor
 * @param initialType  open straight at the steps for this type
 * @param label        button text
 */
export default function RfScanButton({
  iconOnly = false, receiverUno = '', locationId = 0, initialType = '', label = 'Scan RF', sx,
}) {
  const [open, setOpen] = useState(false);
  const [devices, setDevices] = useState([]);
  const [editing, setEditing] = useState(null);

  const start = async (e) => {
    if (e) e.stopPropagation();
    setDevices(await fetchAll({}));
    setOpen(true);
  };

  const afterSave = async ({ RfDeviceId, transmitterId }) => {
    invalidateRfDirectory();
    const list = await fetchAll({});
    const found = list.find((d) => (RfDeviceId && Number(d.RfDeviceId) === RfDeviceId)
      || Number(d.TransmitterId) === transmitterId);
    if (found) setEditing(found);
  };

  return (
    <>
      {iconOnly ? (
        <Tooltip title="Scan an RF sensor">
          <IconButton onClick={start} sx={sx}><SensorsIcon /></IconButton>
        </Tooltip>
      ) : (
        <Button size="small" variant="outlined" startIcon={<SensorsIcon />} onClick={start} sx={sx}>
          {label}
        </Button>
      )}
      <RfScanDialog
        open={open}
        receiverUno={receiverUno}
        locationId={locationId}
        initialType={initialType}
        devices={devices}
        onClose={() => setOpen(false)}
        onSaved={afterSave}
      />
      {/* Rooms are fetched by the dialog from the sensor's own location. */}
      <RfSensorDialog
        device={editing}
        locationId={editing?.LocationID || locationId || 0}
        onClose={() => setEditing(null)}
        onSaved={invalidateRfDirectory}
      />
    </>
  );
}
